import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { isLoginHost } from "./utils/loginHost";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider } from "./context/AuthContext";
import { PermissionsProvider } from "./context/PermissionsContext";
import { ActivityTrackerProvider } from "./context/ActivityTrackerContext";
import { TranslationProvider } from "./context/TranslationContext";
import { LeadCaptureProvider } from "./context/LeadCaptureContext";
import { HelmetProvider } from "react-helmet-async";
import usePageTracking from "./hooks/usePageTracking";
import { Toaster } from "./components/ui/sonner";
import SessionLockScreen from "./components/SessionLockScreen";
import Header from "./components/Header";
import Footer from "./components/Footer";
import "./styles/homepage-animations.css";

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary-blue border-t-transparent rounded-full animate-spin" />
  </div>
);

const BannersPopupsDisplay = lazy(() => import("./components/BannersPopupsDisplay"));
const Home = lazy(() => import("./pages/Home"));
const HomeNew = lazy(() => import("./pages/HomeNew"));
const Outsourcing = lazy(() => import("./pages/Outsourcing"));
const OutsourcingReviewDetail = lazy(() => import("./pages/OutsourcingReviewDetail"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CreditScores = lazy(() => import("./pages/CreditScores"));
const WhyUs = lazy(() => import("./pages/WhyUs"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const AboutCredlocity = lazy(() => import("./pages/AboutCredlocity"));
const PartnersHub = lazy(() => import("./pages/partners/PartnersHub"));
const RealEstatePartner = lazy(() => import("./pages/partners/RealEstatePartner"));
const MortgageProfessionals = lazy(() => import("./pages/partners/MortgageProfessionals"));
const CarDealerships = lazy(() => import("./pages/partners/CarDealerships"));
const SocialMediaInfluencers = lazy(() => import("./pages/partners/SocialMediaInfluencers"));
const AttorneyPartners = lazy(() => import("./pages/partners/AttorneyPartners"));
const CollectionRemoval = lazy(() => import("./pages/credit-issues/CollectionRemoval"));
const LatePaymentRemoval = lazy(() => import("./pages/credit-issues/LatePaymentRemoval"));
const ChargeOffRemoval = lazy(() => import("./pages/credit-issues/ChargeOffRemoval"));
const BankruptcyCreditRepair = lazy(() => import("./pages/credit-issues/BankruptcyCreditRepair"));
const IdentityTheftCreditRepair = lazy(() => import("./pages/credit-issues/IdentityTheftCreditRepair"));
const HardInquiryRemoval = lazy(() => import("./pages/credit-issues/HardInquiryRemoval"));
const SubmitComplaint = lazy(() => import("./pages/SubmitComplaint"));
const ReportCROAViolation = lazy(() => import("./pages/ReportCROAViolation"));
const SuccessStoriesDynamic = lazy(() => import("./pages/SuccessStoriesDynamic"));
const SuccessStoryDetail = lazy(() => import("./pages/SuccessStoryDetail"));
const BlogHub = lazy(() => import("./pages/BlogHub"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Team = lazy(() => import("./pages/Team"));
const AuthorProfile = lazy(() => import("./pages/AuthorProfile"));
const JoezielBioPage = lazy(() => import("./pages/JoezielBioPage"));
const SharBioPage = lazy(() => import("./pages/SharBioPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const LawsuitsPage = lazy(() => import("./pages/LawsuitsPage"));
const LawsuitDetail = lazy(() => import("./pages/LawsuitDetail"));
const PressReleasesPage = lazy(() => import("./pages/PressReleasesPage"));
const PressReleaseDetail = lazy(() => import("./pages/PressReleaseDetail"));
const AnnouncementDetail = lazy(() => import("./pages/AnnouncementDetail"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PartnerDetail = lazy(() => import("./pages/PartnerDetail"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const AttorneySignup = lazy(() => import("./pages/AttorneySignup"));
const CaseUpdatePage = lazy(() => import("./pages/attorney/CaseUpdates"));
const AttorneyCases = lazy(() => import("./pages/attorney/AttorneyCases"));
const AttorneyPayments = lazy(() => import("./pages/attorney/AttorneyPayments"));
const AttorneyCaseDetail = lazy(() => import("./pages/attorney/AttorneyCaseDetail"));
const CreditRepairReviews = lazy(() => import("./pages/CreditRepairReviews"));
const ReviewDetailPage = lazy(() => import("./pages/ReviewDetailPage"));
const ClientReviewFormEnhanced = lazy(() => import("./pages/ClientReviewFormEnhanced"));
const SubmitReview = lazy(() => import("./pages/SubmitReview"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const FreeTrialPage = lazy(() => import("./pages/FreeTrialPage"));
const FraudRemoval = lazy(() => import("./pages/credit-issues/FraudRemoval"));
const HumanTraffickingBlock = lazy(() => import("./pages/credit-issues/HumanTraffickingBlock"));
const CreditRepairScams = lazy(() => import("./pages/CreditRepairScams"));
const CreditTrackerApp = lazy(() => import("./pages/CreditTrackerApp"));
const SchoolApp = lazy(() => import("./pages/SchoolApp"));
const EducationHub = lazy(() => import("./pages/education/EducationHub"));
const EducationCenter = lazy(() => import('./pages/education/EducationCenter'));
const CreditBuilding = lazy(() => import("./pages/education/CreditBuilding"));
const CreditReports = lazy(() => import("./pages/education/CreditReports"));
const RepairMethods = lazy(() => import("./pages/education/RepairMethods"));
const DebtManagement = lazy(() => import("./pages/education/DebtManagement"));
const FinancialWellness = lazy(() => import("./pages/education/FinancialWellness"));
const FCRAGuide = lazy(() => import("./pages/education/FCRAGuide"));
const FCRA611 = lazy(() => import("./pages/education/FCRA611"));
const FCRA609Guide = lazy(() => import("./pages/education/FCRA609Guide"));
const FCRA623Guide = lazy(() => import("./pages/education/FCRA623Guide"));
const LVNVRemoval = lazy(() => import("./pages/education/LVNVRemoval"));
const PortfolioRecoveryRemoval = lazy(() => import("./pages/education/PortfolioRecoveryRemoval"));
const GoodwillDeletionGuide = lazy(() => import("./pages/education/GoodwillDeletionGuide"));
const FDCPAGuide = lazy(() => import("./pages/education/FDCPAGuide"));
const CROAGuide = lazy(() => import("./pages/education/CROAGuide"));
const FCBAGuide = lazy(() => import("./pages/education/FCBAGuide"));
const ChatComingSoon = lazy(() => import("./pages/public/ChatComingSoon"));
const NotFound = lazy(() => import("./pages/public/NotFound"));
const TSRCompliance = lazy(() => import("./pages/education/TSRCompliance"));
const FCRA605B = lazy(() => import("./pages/education/FCRA605B"));
const CreditScoreBoost = lazy(() => import("./pages/education/CreditScoreBoost"));
const PayForDeleteGuide = lazy(() => import("./pages/education/PayForDeleteGuide"));
const DebtValidationLetter = lazy(() => import("./pages/education/DebtValidationLetter"));
const MedicalDebtCredit = lazy(() => import("./pages/education/MedicalDebtCredit"));
const CreditRepairAfterBankruptcy = lazy(() => import("./pages/education/CreditRepairAfterBankruptcy"));
const HowLongCreditRepairTakes = lazy(() => import("./pages/education/HowLongCreditRepairTakes"));
const CreditScoreForMortgage = lazy(() => import("./pages/education/CreditScoreForMortgage"));
const CreditRepairVsDebtConsolidation = lazy(() => import("./pages/education/CreditRepairVsDebtConsolidation"));
const WhatIsACreditScore = lazy(() => import("./pages/education/WhatIsACreditScore"));
const CreditBureauDispute = lazy(() => import("./pages/education/CreditBureauDispute"));
const CreditRepairForVeterans = lazy(() => import("./pages/education/CreditRepairForVeterans"));
const CreditRepairFirstTimeHomeBuyer = lazy(() => import("./pages/education/CreditRepairFirstTimeHomeBuyer"));
const RemoveRepossession = lazy(() => import("./pages/education/RemoveRepossession"));
const CreditUtilizationGuide = lazy(() => import("./pages/education/CreditUtilizationGuide"));
const CreditRepairCost = lazy(() => import("./pages/education/CreditRepairCost"));
const InquiryRemovalGuide = lazy(() => import("./pages/education/InquiryRemovalGuide"));
const StudentLoanCreditRepair = lazy(() => import("./pages/education/StudentLoanCreditRepair"));
const ForeclosureCreditRepair = lazy(() => import("./pages/education/ForeclosureCreditRepair"));
const CreditRepairNearMe = lazy(() => import("./pages/education/CreditRepairNearMe"));
const DebtSettlementVsCreditRepair = lazy(() => import("./pages/education/DebtSettlementVsCreditRepair"));
const FreeLetters = lazy(() => import("./pages/FreeLetters"));
const LetterTemplatePage = lazy(() => import("./pages/LetterTemplatePage"));
const CompetitorComparison = lazy(() => import("./pages/comparisons/CompetitorComparison"));
const CreditRepairReviewsHub = lazy(() => import("./pages/comparisons/CreditRepairReviews"));
const SwitchAndSave = lazy(() => import("./pages/SwitchAndSave"));
const ScamChecker = lazy(() => import("./pages/ScamChecker"));
const FreeCreditReportReview = lazy(() => import("./pages/FreeCreditReportReview"));
const DisputeLetter609 = lazy(() => import("./pages/DisputeLetter609"));
const DisputeCollectionsHub = lazy(() => import("./pages/guides/DisputeCollectionsHub"));
const DisputeCollectionAgency = lazy(() => import("./pages/guides/DisputeCollectionAgency"));
const CalculatorPage = lazy(() => import("./pages/tools/CalculatorPage"));
const CreditScoreSimulator = lazy(() => import("./pages/tools/CreditScoreSimulator"));
// Dedicated DTI calculator page with full SEO, 5 schemas, and interactive calculator
const DebtToIncomeCalculator = lazy(() => import("./pages/tools/DebtToIncomeCalculator"));
const CreditUtilizationCalculator = lazy(() => import("./pages/tools/CreditUtilizationCalculator"));
const AttorneyMarketplaceLanding = lazy(() => import("./pages/public/AttorneyMarketplaceLanding"));
const CROMarketplaceLanding = lazy(() => import("./pages/public/CROMarketplaceLanding"));
const SubmitCase = lazy(() => import("./pages/public/SubmitCase"));
const AttorneyProfile = lazy(() => import("./pages/public/AttorneyProfile"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const SalesDashboard = lazy(() => import("./pages/admin/sales/SalesDashboard"));
const ForgotPassword = lazy(() => import("./pages/admin/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const LoginHandoff = lazy(() => import("./pages/admin/auth/LoginHandoff"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const ClientIntakeForm = lazy(() => import("./pages/public/ClientIntakeForm"));
const AttorneyLogin = lazy(() => import("./pages/attorney/AttorneyLogin"));
const AttorneyChangePassword = lazy(() => import("./pages/attorney/AttorneyChangePassword"));
const AttorneyDemoRedeem = lazy(() => import("./pages/public/AttorneyDemoRedeem"));
const AttorneyPortalLayout = lazy(() => import("./pages/attorney/AttorneyPortalLayout"));
// AMP Build 3 — attorney booking pages (public link pages + portal book-a-call)
const BookingPage = lazy(() => import("./pages/public/BookingPage"));
const ManageBookingPage = lazy(() => import("./pages/public/ManageBookingPage"));
// Calendar Phase 1 — public scheduling page (/schedule/:slug)
const CalendarBookingPage = lazy(() => import("./pages/public/CalendarBookingPage"));
// Calendar Wave 2 — public booking manage page (/schedule/manage)
const CalendarManagePage = lazy(() => import("./pages/public/CalendarManagePage"));
const BookCallPage = lazy(() => import("./pages/attorney/BookCallPage"));
const AttorneyNotFound = lazy(() => import("./pages/attorney/AttorneyNotFound"));
const AttorneyDashboard = lazy(() => import("./pages/attorney/AttorneyDashboard"));
const CaseMarketplace = lazy(() => import("./pages/attorney/CaseMarketplace"));
const CasePledge = lazy(() => import("./pages/attorney/CasePledge"));
const AttorneyReviews = lazy(() => import("./pages/attorney/AttorneyReviews"));
const AttorneySettings = lazy(() => import("./pages/attorney/AttorneySettings"));
const AttorneyPatterns = lazy(() => import("./pages/attorney/AttorneyPatterns"));
const AttorneyProfileEditor = lazy(() => import("./pages/attorney/AttorneyProfileEditor"));
const CROLogin = lazy(() => import("./pages/cro/CROLogin"));
const CROChangePassword = lazy(() => import("./pages/cro/CROChangePassword"));
const CRODashboard = lazy(() => import("./pages/cro/CRODashboard"));
const CROCardSave = lazy(() => import("./pages/cro/CROCardSave"));
const CROLandingPage = lazy(() => import("./pages/cro/CROLandingPage"));
const OutsourcingLogin = lazy(() => import("./pages/partner/OutsourcingLogin"));
const PartnerDashboard = lazy(() => import("./pages/partner/PartnerDashboard"));
const APIDocs = lazy(() => import("./pages/partner/APIDocs"));
const AffiliateLogin = lazy(() => import("./pages/affiliate/AffiliateLogin"));
const AffiliateDashboard = lazy(() => import("./pages/affiliate/AffiliateDashboard"));
const AffiliateLeads = lazy(() => import("./pages/affiliate/AffiliateLeads"));
const AffiliateReferrals = lazy(() => import("./pages/affiliate/AffiliateReferrals"));
const AffiliatePayouts = lazy(() => import("./pages/affiliate/AffiliatePayouts"));
const AffiliateMaterials = lazy(() => import("./pages/affiliate/AffiliateMaterials"));
const AffiliateProfile = lazy(() => import("./pages/affiliate/AffiliateProfile"));
const EbooksPage = lazy(() => import("./pages/EbooksPage"));
const EbookDetailPage = lazy(() => import("./pages/EbookDetailPage"));
const CreditBuilderPublicStore = lazy(() => import("./pages/public/CreditBuilderPublicStore"));
const AffiliateLandingPage = lazy(() => import("./pages/public/AffiliateLandingPage"));
const AffiliateDirectory = lazy(() => import("./pages/public/AffiliateDirectory"));
const CertificateVerify = lazy(() => import("./pages/public/CertificateVerify"));
const LocalLandingPage = lazy(() => import("./pages/public/LocalLandingPage"));
const LocationsPage = lazy(() => import("./pages/LocationsPage"));
const CityFirstTimeHomeBuyer = lazy(() => import("./pages/CityFirstTimeHomeBuyer"));
const CollectionPaymentPortal = lazy(() => import("./pages/public/CollectionPaymentPortal"));
const DeveloperDocs = lazy(() => import('./pages/public/DeveloperDocs'));
const ComplaintHistory = lazy(() => import("./pages/public/ComplaintHistory"));
const MilitaryCreditRepair = lazy(() => import("./pages/public/MilitaryCreditRepair"));
const SCRAGuide = lazy(() => import("./pages/public/SCRAGuide"));
const SignAgreement = lazy(() => import("./pages/SignAgreement"));
const ClientSignAgreement = lazy(() => import("./pages/public/ClientSignAgreement"));
const OutsourcingSignPage = lazy(() => import("./pages/public/OutsourcingSignPage"));
const PaymentSuccess = lazy(() => import("./pages/payment/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/payment/PaymentCancel"));
const PortalLinks = lazy(() => import("./pages/PortalLinks"));
const SEOContentPage = lazy(() => import("./pages/SEOContentPage"));

function PageTracker() {
  usePageTracking();
  return null;
}

const SEO_SLUGS = [
  'enhanced-recovery-removal','convergent-outsourcing-removal',
  'ic-system-removal','credit-corp-solutions-removal','unifin-inc-removal',
  'transworld-systems-removal','americollect-removal','harris-and-harris-removal',
  'national-recovery-agency-removal','nationwide-credit-inc-removal','pra-group-advanced-removal',
  'csc-credit-services-removal','stallion-collection-removal','accounts-receivable-management-removal',
  'midland-credit-removal',
  'debt-validation-letter','pay-for-delete-guide',
  'chexsystems-dispute','lexisnexis-credit-dispute',
  'vantagescore-vs-fico-guide','credit-repair-after-divorce','credit-repair-to-buy-house',
  'investigations','life-events-credit-repair',
];

function AdminAuthWrapper({ children }) {
  const location = useLocation();
  if (location.pathname.startsWith('/partner')) {
    return <>{children}</>;
  }
  return (
    <AuthProvider>
      <PermissionsProvider>
        <ActivityTrackerProvider>
          {children}
          <SessionLockScreen />
        </ActivityTrackerProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <TranslationProvider>
      <LeadCaptureProvider>
        <BrowserRouter>
          <ScrollToTop />
          <PageTracker />
          <AdminAuthWrapper>
            <div className="App">
              <Suspense fallback={<PageLoader />}>
              {/* Login Batch 2 — host-based routing. On login.credlocity.com the app serves ONLY the
                  employee login screen (+ its forgot/reset); every other path redirects to the login
                  screen and no marketing route is ever served there. isLoginHost() is false on www, so
                  the normal route tree below is byte-identical on the public site (and before DNS). */}
              {isLoginHost() ? (
              <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<ForgotPassword />} />
                <Route path="/admin/reset-password" element={<ResetPassword />} />
                <Route path="*" element={<Navigate to="/admin/login" replace />} />
              </Routes>
              ) : (
              <Routes>

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/handoff" element={<LoginHandoff />} />
              <Route path="/admin/forgot-password" element={<ForgotPassword />} />
              <Route path="/admin/reset-password" element={<ResetPassword />} />
              <Route path="/admin/*" element={<AdminDashboard />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/portals" element={<PortalLinks />} />
              <Route path="/company/login" element={<Navigate to="/cro/login" replace />} />
              <Route path="/company/signup" element={<Navigate to="/cro/login" replace />} />
              <Route path="/company/*" element={<Navigate to="/cro" replace />} />
              <Route path="/marketplace/login" element={<Navigate to="/cro/login" replace />} />
              <Route path="/marketplace/signup" element={<Navigate to="/cro/login" replace />} />
              <Route path="/marketplace/*" element={<Navigate to="/cro" replace />} />
              <Route path="/partner" element={<PartnerDashboard />} />
              <Route path="/partner/login" element={<OutsourcingLogin />} />
                <Route path="/partner/api-docs" element={<APIDocs />} />
              <Route path="/partner/:partnerId/*" element={<PartnerDashboard />} />
              {/* IDENTITY M1 (Oct 7 2026): outsourcing-employee portal removed — one person, one login.
                  All /employee/* URLs redirect to the main login; outsourcing work lives in the admin CMS. */}
              <Route path="/employee/*" element={<Navigate to="/admin/login" replace />} />
              <Route path="/affiliate/login" element={<AffiliateLogin />} />
              <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
              <Route path="/affiliate/leads" element={<AffiliateLeads />} />
              <Route path="/affiliate/referrals" element={<AffiliateReferrals />} />
              <Route path="/affiliate/payouts" element={<AffiliatePayouts />} />
              <Route path="/affiliate/materials" element={<AffiliateMaterials />} />
              <Route path="/affiliate/profile" element={<AffiliateProfile />} />
              <Route path="/cro/login" element={<CROLogin />} />
              <Route path="/cro/change-password" element={<CROChangePassword />} />
              <Route path="/cro/save-card" element={<CROCardSave />} />
              <Route path="/cro/*" element={<CRODashboard />} />
              <Route path="/attorney/login" element={<AttorneyLogin />} />
              <Route path="/attorney/change-password" element={<AttorneyChangePassword />} />
              <Route path="/attorney/demo" element={<AttorneyDemoRedeem />} />
              <Route path="/sign/:signToken" element={<SignAgreement />} />
              <Route path="/csign/:signToken" element={<ClientSignAgreement />} />
              <Route path="/sign/outsourcing/:token" element={<OutsourcingSignPage />} />
              <Route element={<AttorneyPortalLayout />}>
                <Route path="/attorney/marketplace" element={<CaseMarketplace />} />
                <Route path="/attorney/cases/:caseId/pledge" element={<CasePledge />} />
                <Route path="/attorney/case-updates/:caseId" element={<CaseUpdatePage />} />
                <Route path="/attorney/case-updates" element={<CaseUpdatePage />} />
                <Route path="/attorney/cases/:caseId" element={<AttorneyCaseDetail />} />
                <Route path="/attorney/cases" element={<AttorneyCases />} />
                <Route path="/attorney/payments" element={<AttorneyPayments />} />
                <Route path="/attorney/reviews" element={<AttorneyReviews />} />
                <Route path="/attorney/settings" element={<AttorneySettings />} />
                <Route path="/attorney/patterns" element={<AttorneyPatterns />} />
                <Route path="/attorney/profile" element={<AttorneyProfileEditor />} />
                <Route path="/attorney/book-call" element={<BookCallPage />} />
                <Route path="/attorney" element={<AttorneyDashboard />} />
                {/* Attorney-scoped catch-all: keeps unmatched /attorney/* inside the portal
                    (rendered via AttorneyPortalLayout's Outlet) instead of falling to the
                    global path="*" marketing 404. Splat is lowest-specificity, so every
                    real attorney route above still wins. */}
                <Route path="/attorney/*" element={<AttorneyNotFound />} />
              </Route>
              <Route path="/intake" element={<ClientIntakeForm />} />
              <Route path="/get-started" element={<ClientIntakeForm />} />
              <Route path="/developer" element={<DeveloperDocs />} />
              <Route path="/credit-assessment" element={<ClientIntakeForm />} />
              <Route path="/attorney/signup" element={<AttorneySignup />} />
              <Route path="/attorneys/apply" element={<AttorneySignup />} />
              {/* AMP Build 3 — public booking (one-time link) + reschedule/cancel (reusable link) */}
              <Route path="/book" element={<><Header /><BookingPage /><Footer /></>} />
              <Route path="/book/manage" element={<><Header /><ManageBookingPage /><Footer /></>} />
              {/* Calendar Wave 2 — manage page (must be before /:slug so it wins) */}
              <Route path="/schedule/manage" element={<><Header /><CalendarManagePage /><Footer /></>} />
              {/* Calendar Phase 1 — public scheduling page */}
              <Route path="/schedule/:slug" element={<><Header /><CalendarBookingPage /><Footer /></>} />
              <Route path="/cro-partnership" element={<CROLandingPage />} />
              <Route path="/attorney-marketplace" element={<><Header /><AttorneyMarketplaceLanding /><Footer /></>} />
              <Route path="/attorney-profile/:attorney_id" element={<AttorneyProfile />} />
              <Route path="/cro-marketplace" element={<><Header /><CROMarketplaceLanding /><Footer /></>} />
              <Route path="/submit-case" element={<SubmitCase />} />
              <Route path="/blog" element={<BlogHub />} />
              <Route path="/post/:slug" element={<BlogPost />} />
              <Route path="/team" element={<Team />} />
              {/* Dedicated bio pages — MUST come before generic /team/:slug */}
              <Route path="/team/joeziel-joey-vazquez-davila" element={<JoezielBioPage />} />
              <Route path="/credlocity-about-us-philadelphia-credit-repair-joeziel-vazquez" element={<JoezielBioPage />} />
              <Route path="/team/shar" element={<SharBioPage />} />
              <Route path="/team/shar-schaffeld" element={<SharBioPage />} />
              <Route path="/team/shar-schaffeld-credit-expert-nampa-idaho" element={<SharBioPage />} />
              <Route path="/team/:slug" element={<AuthorProfile />} />
              <Route path="/faqs" element={<FAQPage />} />
              <Route path="/faq" element={<Navigate to="/faqs" replace />} />

              {/* SEO Content Pages */}
              {SEO_SLUGS.map(slug => (
                <Route
                  key={slug}
                  path={`/${slug}`}
                  element={<><Header /><SEOContentPage slug={slug} /><Footer /></>}
                />
              ))}

              {/* Public pages */}
              <Route path="/" element={<><Header /><HomeNew /><Footer /></>} />
              <Route path="/home-old" element={<><Header /><Home /><Footer /></>} />

              {/* Legacy URL redirects */}
              <Route path="/plans-pricing" element={<Navigate to="/pricing" replace />} />
              <Route path="/letsstart" element={<Navigate to="/intake" replace />} />
              <Route path="/free-credit-analysis-tsr-compliant-signup" element={<Navigate to="/intake" replace />} />
              <Route path="/credlocity-about-us-philadelphia-credit-repair-joeziel-vazquez" element={<Navigate to="/about-us" replace />} />
              <Route path="/whyuscollectionacconts" element={<Navigate to="/collection-removal" replace />} />
              <Route path="/whyus-fraud-removal" element={<Navigate to="/fraud-removal" replace />} />
              <Route path="/best-credit-repair-company-comparison" element={<Navigate to="/credit-repair-reviews" replace />} />
              <Route path="/consumer-law-credit-repair-philadelphia" element={<Navigate to="/about-us" replace />} />
              <Route path="/credit-repair-free-trial" element={<Navigate to="/30-day-free-trial" replace />} />
              <Route path="/credlocity-credit-repair-library" element={<Navigate to="/education" replace />} />
              <Route path="/fcra-605b-block-request" element={<Navigate to="/fcra-605b-block" replace />} />
              <Route path="/personal-finance-budget-calculator-guide" element={<Navigate to="/tools/budget-calculator" replace />} />
              <Route path="/complete-credit-repair-guide-legal-methods" element={<Navigate to="/complete-credit-repair-guide" replace />} />
              <Route path="/fcra" element={<Navigate to="/fcra-guide" replace />} />
              <Route path="/fdcpa-collection-removal-guide" element={<Navigate to="/fdcpa-guide" replace />} />
              <Route path="/credit-education-complete-guide-credit-scores-reports" element={<Navigate to="/education" replace />} />
              <Route path="/free-credit-repair-guide" element={<Navigate to="/complete-credit-repair-guide" replace />} />
              <Route path="/credit-repair-audit" element={<Navigate to="/intake" replace />} />

              <Route path="/pricing" element={<><Header /><Pricing /><Footer /></>} />
              <Route path="/become-a-partner" element={<><Header /><PartnersHub /><Footer /></>} />
              <Route path="/become-a-partner/real-estate" element={<><Header /><RealEstatePartner /><Footer /></>} />
              <Route path="/become-a-partner/mortgage-professionals" element={<><Header /><MortgageProfessionals /><Footer /></>} />
              <Route path="/become-a-partner/car-dealerships" element={<><Header /><CarDealerships /><Footer /></>} />
              <Route path="/become-a-partner/social-media-influencers" element={<><Header /><SocialMediaInfluencers /><Footer /></>} />
              <Route path="/become-a-partner/attorneys" element={<><Header /><AttorneyPartners /><Footer /></>} />
              <Route path="/credit-scores" element={<><Header /><CreditScores /><Footer /></>} />
              <Route path="/why-us" element={<><Header /><WhyUs /><Footer /></>} />
              <Route path="/about-us" element={<><Header /><AboutUs /><Footer /></>} />
              <Route path="/about-credlocity" element={<><Header /><AboutCredlocity /><Footer /></>} />
              <Route path="/collection-removal" element={<><Header /><CollectionRemoval /><Footer /></>} />
              <Route path="/late-payment-removal" element={<><Header /><LatePaymentRemoval /><Footer /></>} />
              <Route path="/charge-off-removal" element={<><Header /><ChargeOffRemoval /><Footer /></>} />
              <Route path="/bankruptcy-credit-repair" element={<><Header /><BankruptcyCreditRepair /><Footer /></>} />
              <Route path="/identity-theft-credit-repair" element={<><Header /><IdentityTheftCreditRepair /><Footer /></>} />
              <Route path="/hard-inquiry-removal" element={<><Header /><HardInquiryRemoval /><Footer /></>} />
              <Route path="/report-company" element={<><Header /><SubmitComplaint /><Footer /></>} />
              <Route path="/report-croa-violation" element={<><Header /><ReportCROAViolation /><Footer /></>} />
              <Route path="/submit-complaint" element={<><Header /><SubmitComplaint /><Footer /></>} />
              <Route path="/credit-repair-complaints" element={<CreditRepairReviews />} />
              <Route path="/credit-repair-complaints/review/:reviewSlug" element={<ReviewDetailPage />} />
              <Route path="/credit-repair-complaints/:companySlug" element={<CreditRepairReviews />} />
              <Route path="/success-stories" element={<><Header /><SuccessStoriesDynamic /><Footer /></>} />
              <Route path="/success-stories/:slug" element={<><Header /><SuccessStoryDetail /><Footer /></>} />
              <Route path="/leave-review" element={<><Header /><ClientReviewFormEnhanced /><Footer /></>} />
              <Route path="/review/:token" element={<ClientReviewFormEnhanced />} />
              <Route path="/lawsuits" element={<LawsuitsPage />} />
              <Route path="/lawsuits/:slug" element={<LawsuitDetail />} />
              <Route path="/press-releases" element={<PressReleasesPage />} />
              <Route path="/press-releases/:slug" element={<PressReleaseDetail />} />
              <Route path="/announcements/:slug" element={<AnnouncementDetail />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/partners/:slug" element={<PartnerDetail />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/outsourcing" element={<Outsourcing />} />
              <Route path="/outsourcing/reviews/:slug" element={<OutsourcingReviewDetail />} />
              <Route path="/submit-review/:token" element={<SubmitReview />} />
              <Route path="/how-it-works" element={<><Header /><HowItWorks /><Footer /></>} />
              <Route path="/30-day-free-trial" element={<><Header /><FreeTrialPage /><Footer /></>} />
              <Route path="/fraud-removal" element={<><Header /><FraudRemoval /><Footer /></>} />
              <Route path="/human-trafficking-credit-block" element={<><Header /><HumanTraffickingBlock /><Footer /></>} />
              <Route path="/credit-repair-scams" element={<><Header /><CreditRepairScams /><Footer /></>} />
              <Route path="/credit-tracker-app" element={<><Header /><CreditTrackerApp /><Footer /></>} />
              <Route path="/store" element={<><Header /><EbooksPage /><Footer /></>} />
              <Route path="/store/:slug" element={<><Header /><EbookDetailPage /><Footer /></>} />
              <Route path="/ebooks" element={<Navigate to="/store" replace />} />
              <Route path="/credit-builder-store" element={<><Header /><CreditBuilderPublicStore /><Footer /></>} />
              <Route path="/education" element={<><Header /><EducationCenter /><Footer /></>} />
              <Route path="/education-hub" element={<Navigate to="/education" replace />} />
              <Route path="/credit-repair-laws" element={<Navigate to="/education" replace />} />
              <Route path="/credit-building" element={<><Header /><CreditBuilding /><Footer /></>} />
              <Route path="/credit-reports" element={<><Header /><CreditReports /><Footer /></>} />
              <Route path="/repair-methods" element={<><Header /><RepairMethods /><Footer /></>} />
              <Route path="/debt-management" element={<><Header /><DebtManagement /><Footer /></>} />
              <Route path="/financial-wellness" element={<><Header /><FinancialWellness /><Footer /></>} />
              <Route path="/fcra-guide" element={<><Header /><FCRAGuide /><Footer /></>} />
              <Route path="/fcra-611" element={<><Header /><FCRA611 /><Footer /></>} />
              <Route path="/fcra-609" element={<><Header /><FCRA609Guide /><Footer /></>} />
              <Route path="/fcra-623-direct-dispute" element={<><Header /><FCRA623Guide /><Footer /></>} />
              <Route path="/fdcpa-guide" element={<><Header /><FDCPAGuide /><Footer /></>} />
              <Route path="/croa-guide" element={<><Header /><CROAGuide /><Footer /></>} />
              <Route path="/fcba" element={<><Header /><FCBAGuide /><Footer /></>} />
              <Route path="/chat" element={<ChatComingSoon />} />
              <Route path="/tsr-compliance" element={<><Header /><TSRCompliance /><Footer /></>} />
              <Route path="/credit-score-boost" element={<><Header /><CreditScoreBoost /><Footer /></>} />
              <Route path="/pay-for-delete-letter" element={<><Header /><PayForDeleteGuide /><Footer /></>} />
              <Route path="/education/debt-validation-letter" element={<><Header /><DebtValidationLetter /><Footer /></>} />
              <Route path="/education/medical-debt-credit-report" element={<><Header /><MedicalDebtCredit /><Footer /></>} />
              <Route path="/education/credit-repair-after-bankruptcy" element={<><Header /><CreditRepairAfterBankruptcy /><Footer /></>} />
              <Route path="/education/how-long-does-credit-repair-take" element={<><Header /><HowLongCreditRepairTakes /><Footer /></>} />
              <Route path="/education/credit-score-for-mortgage" element={<><Header /><CreditScoreForMortgage /><Footer /></>} />
              <Route path="/education/credit-repair-vs-debt-consolidation" element={<><Header /><CreditRepairVsDebtConsolidation /><Footer /></>} />
              <Route path="/education/what-is-a-credit-score" element={<><Header /><WhatIsACreditScore /><Footer /></>} />
              <Route path="/education/credit-bureau-dispute" element={<><Header /><CreditBureauDispute /><Footer /></>} />
              <Route path="/education/credit-repair-for-veterans" element={<><Header /><CreditRepairForVeterans /><Footer /></>} />
              <Route path="/education/credit-repair-first-time-home-buyer" element={<><Header /><CreditRepairFirstTimeHomeBuyer /><Footer /></>} />
              <Route path="/education/remove-repossession-from-credit-report" element={<><Header /><RemoveRepossession /><Footer /></>} />
              <Route path="/education/credit-utilization-guide" element={<><Header /><CreditUtilizationGuide /><Footer /></>} />
              <Route path="/education/how-much-does-credit-repair-cost" element={<><Header /><CreditRepairCost /><Footer /></>} />
              <Route path="/education/how-to-remove-hard-inquiries" element={<><Header /><InquiryRemovalGuide /><Footer /></>} />
              <Route path="/education/student-loan-credit-repair" element={<><Header /><StudentLoanCreditRepair /><Footer /></>} />
              <Route path="/education/credit-repair-after-foreclosure" element={<><Header /><ForeclosureCreditRepair /><Footer /></>} />
              <Route path="/education/credit-repair-near-me" element={<><Header /><CreditRepairNearMe /><Footer /></>} />
              <Route path="/education/debt-settlement-vs-credit-repair" element={<><Header /><DebtSettlementVsCreditRepair /><Footer /></>} />
              <Route path="/fcra-605b-block" element={<><Header /><FCRA605B /><Footer /></>} />
              <Route path="/lvnv-funding-removal" element={<><Header /><LVNVRemoval /><Footer /></>} />
              <Route path="/portfolio-recovery-removal" element={<><Header /><PortfolioRecoveryRemoval /><Footer /></>} />
              <Route path="/goodwill-deletion-letter" element={<><Header /><GoodwillDeletionGuide /><Footer /></>} />
              <Route path="/free-letters" element={<><Header /><FreeLetters /><Footer /></>} />
              <Route path="/free-letters/:slug" element={<><Header /><LetterTemplatePage /><Footer /></>} />
              <Route path="/credit-repair-reviews" element={<><Header /><CreditRepairReviewsHub /><Footer /></>} />
              <Route path="/switch" element={<><Header /><SwitchAndSave /><Footer /></>} />
              <Route path="/scam-checker" element={<><Header /><ScamChecker /><Footer /></>} />
              <Route path="/free-credit-report-review" element={<><Header /><FreeCreditReportReview /><Footer /></>} />
              <Route path="/609-dispute-letter" element={<><Header /><DisputeLetter609 /><Footer /></>} />
              <Route path="/how-to-dispute-collections" element={<><Header /><DisputeCollectionsHub /><Footer /></>} />
              <Route path="/dispute/:slug" element={<><Header /><DisputeCollectionAgency /><Footer /></>} />
              <Route path="/vs-lexington-law" element={<><Header /><CompetitorComparison competitor="lexington-law" /><Footer /></>} />
              <Route path="/vs-creditrepair" element={<><Header /><CompetitorComparison competitor="creditrepair-com" /><Footer /></>} />
              <Route path="/vs-credit-people" element={<><Header /><CompetitorComparison competitor="credit-people" /><Footer /></>} />
              <Route path="/vs-credit-pros" element={<><Header /><CompetitorComparison competitor="credit-pros" /><Footer /></>} />
              <Route path="/vs-credit-saint" element={<><Header /><CompetitorComparison competitor="credit-saint" /><Footer /></>} />
              <Route path="/vs-white-jacobs" element={<><Header /><CompetitorComparison competitor="white-jacobs" /><Footer /></>} />
              <Route path="/tools/credit-score-simulator" element={<><Header /><CreditScoreSimulator /><Footer /></>} />
              <Route path="/tools/credit-score-calculator" element={<><Header /><CreditScoreSimulator /><Footer /></>} />
              {/* DTI Calculator — dedicated full-SEO page (replaces generic CalculatorPage) */}
              <Route path="/tools/debt-to-income-calculator" element={<DebtToIncomeCalculator />} />
              <Route path="/tools/credit-utilization-calculator" element={<><Header /><CreditUtilizationCalculator /><Footer /></>} />
              <Route path="/tools/loan-payment-calculator" element={<><Header /><CalculatorPage type="loan-payment" /><Footer /></>} />
              <Route path="/tools/debt-payoff-calculator" element={<><Header /><CalculatorPage type="debt-payoff" /><Footer /></>} />
              <Route path="/tools/mortgage-calculator" element={<><Header /><CalculatorPage type="mortgage" /><Footer /></>} />
              <Route path="/tools/credit-card-payoff-calculator" element={<><Header /><CalculatorPage type="credit-card-payoff" /><Footer /></>} />
              <Route path="/tools/savings-calculator" element={<><Header /><CalculatorPage type="savings" /><Footer /></>} />
              <Route path="/tools/interest-calculator" element={<><Header /><CalculatorPage type="interest" /><Footer /></>} />
              <Route path="/tools/budget-calculator" element={<><Header /><CalculatorPage type="budget" /><Footer /></>} />
              <Route path="/tools/refinance-calculator" element={<><Header /><CalculatorPage type="refinance" /><Footer /></>} />
              <Route path="/tools/student-loan-calculator" element={<><Header /><CalculatorPage type="student-loan" /><Footer /></>} />
              <Route path="/tools/auto-loan-calculator" element={<><Header /><CalculatorPage type="auto-loan" /><Footer /></>} />
              <Route path="/tools/retirement-calculator" element={<><Header /><CalculatorPage type="retirement" /><Footer /></>} />
              <Route path="/verify" element={<CertificateVerify />} />
              <Route path="/verify/:certificateId" element={<CertificateVerify />} />
              <Route path="/p/:slug" element={<AffiliateLandingPage />} />
              <Route path="/affiliate-partners" element={<><Header /><AffiliateDirectory /><Footer /></>} />
              <Route path="/locations" element={<><Header /><LocationsPage /><Footer /></>} />
              <Route path="/:slug/first-time-home-buyer" element={<><Header /><CityFirstTimeHomeBuyer /><Footer /></>} />
              <Route path="/school" element={<SchoolApp />} />
              <Route path="/school/:page" element={<SchoolApp />} />
              <Route path="/school/:page/:courseId" element={<SchoolApp />} />

              <Route path="/credit-repair-for-veterans-military" element={<><Header /><MilitaryCreditRepair /><Footer /></>} />
              <Route path="/servicemembers-civil-relief-act-scra-credit-rights" element={<><Header /><SCRAGuide /><Footer /></>} />
              <Route path="/complaint-history" element={<ComplaintHistory />} />

              {/* Wildcard catch-all — must be LAST */}
              <Route path="/:slug" element={<><Header /><LocalLandingPage /><Footer /></>} />
              <Route path="/pay/:token" element={<CollectionPaymentPortal />} />

              {/* Catch-all: unknown MULTI-segment URLs (single-segment ones are caught by /:slug
                  above). In react-router v7 this splat is the lowest-priority match, so it never
                  steals a valid route. Renders a real noindex NotFound page instead of a blank 200. */}
              <Route path="*" element={<><Header /><NotFound /><Footer /></>} />

              </Routes>
              )}
              </Suspense>
              <Toaster />
              <Suspense fallback={null}><BannersPopupsDisplay /></Suspense>
            </div>
          </AdminAuthWrapper>
        </BrowserRouter>
      </LeadCaptureProvider>
      </TranslationProvider>
    </HelmetProvider>
  );
}

export default App;
// SEO pages registered
